import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Divider, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService, staffService } from '@smartgrow/shared';
import type { CreateProjectRequest } from '@smartgrow/shared/types';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { LocationPicker } from '../../components/LocationPicker';

export const EditProjectPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const projectId = id!;

  const [form, setForm] = useState<CreateProjectRequest | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateProjectRequest, string>>>({});

  const { data: project, isLoading: projLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProjectById(projectId),
    enabled: !!projectId,
  });

  const { data: staffData } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => staffService.getStaff({ size: 200 }),
  });
  const staff = staffData?.content ?? [];
  const managers = staff.filter((s) => s.role === 'ADMIN' || s.role === 'SUPERVISOR');

  useEffect(() => {
    if (project) {
      setForm({
        projectName: project.projectName ?? '',
        description: project.description ?? '',
        location: project.location ?? '',
        latitude: project.latitude,
        longitude: project.longitude,
        startDate: project.startDate ?? '',
        endDate: project.endDate ?? '',
        budget: project.budget ?? 0,
        managerId: project.managerId ?? '',
        supervisorId: project.supervisorId ?? undefined,
        notes: project.notes ?? '',
      });
    }
  }, [project]);

  const mutation = useMutation({
    mutationFn: (data: CreateProjectRequest) =>
      projectService.updateProject(projectId, {
        ...data,
        budget: Number(data.budget),
        endDate: data.endDate || undefined,
        supervisorId: data.supervisorId || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      navigate(`/dashboard/projects/${projectId}`);
    },
  });

  const validate = () => {
    if (!form) return false;
    const e: typeof errors = {};
    if (!form.projectName.trim()) e.projectName = 'Project name is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.startDate) e.startDate = 'Start date is required';
    if (!form.budget || Number(form.budget) <= 0) e.budget = 'Budget must be greater than 0';
    if (!form.managerId) e.managerId = 'Manager is required';
    if (form.endDate && form.endDate < form.startDate) e.endDate = 'End date must be after start date';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof CreateProjectRequest, value: any) => {
    setForm((f) => f ? { ...f, [field]: value } : f);
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleSubmit = () => {
    if (!validate() || !form) return;
    mutation.mutate(form);
  };

  if (projLoading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">Edit Project</Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to update project. Please try again.
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>Project Details</Typography>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Project Name"
              value={form.projectName}
              onChange={(e) => set('projectName', e.target.value)}
              fullWidth
              error={!!errors.projectName}
              helperText={errors.projectName}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              fullWidth
              error={!!errors.location}
              helperText={errors.location}
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              label="Latitude"
              type="number"
              value={form.latitude ?? ''}
              onChange={(e) => set('latitude', e.target.value === '' ? undefined : Number.parseFloat(e.target.value))}
              fullWidth
              inputProps={{ step: 'any', min: -90, max: 90 }}
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              label="Longitude"
              type="number"
              value={form.longitude ?? ''}
              onChange={(e) => set('longitude', e.target.value === '' ? undefined : Number.parseFloat(e.target.value))}
              fullWidth
              inputProps={{ step: 'any', min: -180, max: 180 }}
            />
          </Grid>

          <Grid item xs={12}>
            <LocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(lat, lng) => { set('latitude', lat); set('longitude', lng); }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              fullWidth
              error={!!errors.description}
              helperText={errors.description}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Budget (₦)"
              type="number"
              value={form.budget || ''}
              onChange={(e) => set('budget', Number.parseFloat(e.target.value) || 0)}
              fullWidth
              error={!!errors.budget}
              helperText={errors.budget}
              inputProps={{ min: 1, step: 1000 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              fullWidth
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date (optional)"
              type="date"
              value={form.endDate ?? ''}
              onChange={(e) => set('endDate', e.target.value)}
              fullWidth
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Project Manager"
              value={form.managerId || ''}
              onChange={(e) => set('managerId', e.target.value)}
              fullWidth
              error={!!errors.managerId}
              helperText={errors.managerId}
            >
              {managers.length ? (
                managers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.fullName} ({s.role})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No staff found</MenuItem>
              )}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Supervisor (optional)"
              value={form.supervisorId ?? ''}
              onChange={(e) => set('supervisorId', e.target.value || undefined)}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {managers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.fullName} ({s.role})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <AttachmentsPanel entityType="PROJECT" entityId={projectId} />

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate(`/dashboard/projects/${projectId}`)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
